import {
  IResourceComponentsProps,
  useTranslate,
  BaseKey,
  useApiUrl,
} from '@pankod/refine-core';
import React, { useEffect, useState } from 'react';
import {
  Create,
  Form,
  Input,
  InputNumber,
  useForm,
  Row,
  Col,
  Checkbox,
  Select,
  useSelect,
  Button,
  Modal,
  Alert,
  Upload,
  Typography,
  message,
  Card,
  Spin,
} from '@pankod/refine-antd';
import { AimOutlined } from '@ant-design/icons';
import type { UploadFile, UploadProps } from 'antd/es/upload/interface';
import type { RcFile } from 'antd/es/upload';
import { Ownership, User } from 'interfaces';
import UserHandler from 'util/UserHandler';
import { OwnershipType } from 'types/ownership/ownershipType';
import { UserRoleEnum } from 'types/users/userRole';
import { EnumToArray } from 'util/parseData';
import axios from 'axios';
import { Country, State } from 'country-state-city';
import { states as BFStates } from 'util/state';

const { Title } = Typography;
const fileFolder = Math.random().toString(36).slice(-8);

export const OwnershipCreate: React.FC<IResourceComponentsProps> = () => {
  const { formProps, saveButtonProps } = useForm<Ownership>({
    action: 'create',
    resource: 'ownership',
    
  });
  const [form] = Form.useForm();
  const apiUrl = useApiUrl();
  const t = useTranslate();
  const authUser = UserHandler.getInstance().user;
  const [ownershipType, setOwnershipType] = useState<OwnershipType | undefined>(OwnershipType.Apartment);
  const typeOptions = EnumToArray(OwnershipType).map(op => {
    op.label = t(`types.${op.label}`, { ns: 'ownerships' });
    return op;
  });
  const [users, setUsers] = useState<{ value: BaseKey; label: string }[]>([]);
  const [country, setCountry] = useState<{ value: BaseKey; label: string }[]>([]);
  const [states, setStates] = useState<{ value: BaseKey; label: string }[]>([]);
  const [countrySel, setCountrySel] = useState<string>('BF');
  const [stateSel, setStateSel] = useState<string>('');
  const [newUserVisible, setNewUserVisible] = useState(false);
  const [newUserPhone, setNewUserPhone] = useState<string>();
  const [newUserEmail, setNewUserEmail] = useState<string>();
  const [newUserError, setNewUserError] = useState(false);
  const { selectProps: selectPropsUser, queryResult: queryResultUser } = useSelect<User>({
    resource: 'user',
    optionLabel: 'name',
    optionValue: 'id',
  });
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [isGeolocationLoading, setIsGeolocationLoading] = useState(false);

  useEffect(() => {
    setNewUserError(false);
    form?.resetFields();
    onSearchCountry('');
  }, []);

  useEffect(() => {
    onSearchUser('');
  }, [queryResultUser?.data?.data]);

  useEffect(() => {
    onSearchState('');
  }, [countrySel]);

  useEffect(() => {
    console.log('Étape actuelle:', step);
    if (step === 4) {
      console.log('Section Media (Step 4) rendue');
    }
  }, [step]);

  const onSearchUser = (searchText: string) => {
    const res: { value: BaseKey; label: string }[] = [];
    queryResultUser?.data?.data
      ?.filter((user: User) =>
        user.lastname + ' ' + user.firstname !== '- -'
          ? (user.lastname + ' ' + user.firstname).toLowerCase().includes(searchText.toLowerCase())
          : user.phone.toLowerCase().includes(searchText.toLowerCase()),
      )
      .forEach((user: User) => {
        if (user.id) {
          const value: BaseKey = user.id;
          let label: string = (user.lastname + ' ' + user.firstname).trim();
          if (label === '- -') label = user.phone;
          res.push({ value, label });
        }
      });
    setUsers(res);
  };

  const onSearchCountry = (searchText: string) => {
    const res: { value: BaseKey; label: string }[] = [];
    Country.getAllCountries()
      ?.filter((c: any) => c.name.toLowerCase().includes(searchText.toLowerCase()))
      .forEach((c: any) => {
        if (c.isoCode) {
          const value: BaseKey = c.isoCode;
          const label: string = c.name;
          res.push({ value, label });
        }
      });
    setCountry(res);
  };

  const onSearchState = (searchText: string) => {
    const res: { value: BaseKey; label: string }[] = [];
    const list: any[] =
      (countrySel === 'BF'
        ? BFStates.sort((a: any, b: any) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0))
        : State.getStatesOfCountry(countrySel)) || [];
    list
      .filter((c: any) => c.name.toLowerCase().includes(searchText.toLowerCase()))
      .forEach((c: any) => {
        if (c.isoCode) {
          const value: BaseKey = c.isoCode;
          const label: string = c.name;
          res.push({ value, label });
        }
      });
    setStates(res);
  };

  const newUserForm = () => {
    setNewUserEmail(form.getFieldValue('email'));
    setNewUserPhone(form.getFieldValue('phone'));
  };

  const handleOk = async () => {
    setNewUserError(false);
    try {
      await form.validateFields();
      const userForm = Object.assign(form.getFieldsValue(), {
        password: Math.random().toString(36).slice(-8),
        newsletter: false,
        role: UserRoleEnum.User,
        parent_user_id: authUser?.id,
      });
      if (!userForm.phone) delete userForm.phone;
      if (!userForm.email) delete userForm.email;

      const response = await axios
        .post(`${apiUrl}/user`, userForm, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${UserHandler.getInstance().token}`,
          },
        })
        .then(res => res.data)
        .catch(err => err.response);
      if (response?.data?.id) {
        formProps?.form?.setFieldsValue({ user_id: response.data.id });
        queryResultUser?.refetch();
        setUsers(prev => [
          ...prev,
          {
            value: response.data.id,
            label: `${response.data.lastname} ${response.data.firstname}`.trim() || response.data.phone,
          },
        ]);
      } else {
        setNewUserError(true);
        return;
      }
      setNewUserVisible(false);
    } catch (error) {
      setNewUserError(true);
      console.error('New user creation error:', error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setNewUserVisible(false);
  };

  const handleChange: UploadProps['onChange'] = ({ fileList: newFileList }) => {
    console.log('handleChange - Événement déclenché, nouvelle liste de fichiers:', newFileList);
    const updatedFileList = newFileList.map(file => ({
      ...file,
      url: file.response ? `${apiUrl}${file.response.filePath}` : file.url,
    }));
    setFileList(updatedFileList);

    const attachments = updatedFileList
      .filter(file => file.status === 'done' && file.response?.filePath)
      .map(file => ({
        path: file.response.filePath,
        ownership_type: ownershipType,
        custom_type: 'image',
        validation_status: 'pending',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }));

    formProps?.form?.setFieldsValue({ attachments });
    console.log('Attachments mis à jour dans le formulaire:', attachments);
  };

  const handleRemove = (file: UploadFile) => {
    console.log('handleRemove - Tentative de suppression du fichier:', file);
    if (file.response?.filePath) {
      fetch(`${apiUrl}/media/upload`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${UserHandler.getInstance().token}`,
        },
        body: JSON.stringify({
          file: file.response.filePath,
        }),
      })
        .then(response => {
          console.log('handleRemove - Réponse de suppression:', response);
          if (response.ok) {
            const updatedAttachments = fileList
              .filter(f => f.uid !== file.uid && f.response?.filePath)
              .map(f => ({
                path: f.response.filePath,
                ownership_type: ownershipType,
                custom_type: 'image',
                validation_status: 'pending',
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
              }));
            formProps?.form?.setFieldsValue({ attachments: updatedAttachments });
            console.log('Attachments mis à jour après suppression:', updatedAttachments);
          }
          return response.json();
        })
        .catch(error => {
          console.error('handleRemove - Erreur lors de la suppression:', error);
          message.error('Échec de la suppression du fichier');
        });
    }
  };

  const onPreview = async (file: UploadFile) => {
    console.log('onPreview - Fichier à prévisualiser:', file);
    let src = file.url as string;
    if (!src && file.response?.filePath) {
      src = `${apiUrl}${file.response.filePath}`;
    } else if (!src && file.originFileObj) {
      src = await new Promise(resolve => {
        const reader = new FileReader();
        reader.readAsDataURL(file.originFileObj as RcFile);
        reader.onload = () => resolve(reader.result as string);
      });
    }
    const image = new Image();
    image.src = src;
    const imgWindow = window.open(src);
    imgWindow?.document.write(image.outerHTML);
  };

  const handleUserSelect = (value: BaseKey) => {
    formProps?.form?.setFieldsValue({ user_id: value });
  };

  const customRequest = ({ file, onSuccess, onError }: any) => {
    console.log('customRequest - Téléchargement démarré pour:', file.name);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('table', 'attachments/' + fileFolder);

    axios
      .post(`${apiUrl}/media/upload`, formData, {
        headers: {
          Authorization: `Bearer ${UserHandler.getInstance().token}`,
          'Content-Type': 'multipart/form-data',
        },
      })
      .then(response => {
        console.log('customRequest - Succès:', response.data);
        onSuccess(response.data, file);
      })
      .catch(error => {
        console.error('customRequest - Erreur:', error);
        onError(error);
        message.error(`Échec du téléchargement de ${file.name}`);
      });
  };

  const getCurrentLocation = async () => {
    if (!navigator.geolocation) {
      message.error(t('messages.geolocationNotSupported', { ns: 'ownerships', defaultValue: 'La géolocalisation n\'est pas supportée par votre navigateur.' }));
      return;
    }

    setIsGeolocationLoading(true);
    try {
      console.log('Tentative de récupération de la localisation...');
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          resolve,
          reject,
          {
            timeout: 30000,
            maximumAge: 0,
            enableHighAccuracy: true,
          }
        );
      });

      const { latitude, longitude, altitude, accuracy } = position.coords;
      console.log('Coordonnées récupérées:', { latitude, longitude, altitude, accuracy });

      const updatedFields: any = { latitude, longitude, altitude: altitude || null };

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_GEOCODE_API}?latlng=${latitude},${longitude}&key=${process.env.REACT_APP_GEOCODE_API_KEY}`,
        );
        console.log('Réponse Geocoding API:', response.data);

        if (response?.data?.results?.length > 0) {
          const addressComponents = response.data.results[0].address_components;
          const formattedAddress = response.data.results[0].formatted_address;

          let city = '';
          let province = '';
          let countryCode = '';
          let countryName = '';
          let postalCode = '';

          addressComponents.forEach((component: any) => {
            if (component.types.includes('locality')) city = component.long_name;
            if (component.types.includes('administrative_area_level_1')) province = component.long_name;
            if (component.types.includes('country')) {
              countryCode = component.short_name;
              countryName = component.long_name;
            }
            if (component.types.includes('postal_code')) postalCode = component.long_name;
          });

          updatedFields.address = formattedAddress;
          updatedFields.city = city;
          updatedFields.province = province;
          updatedFields.state = countryCode;
          updatedFields.postalCode = postalCode;

          setCountrySel(countryCode);
          setStateSel(province);

          console.log('Champs mis à jour:', {
            address: formattedAddress,
            city,
            province,
            state: countryCode,
            postalCode,
            countryName,
          });
        } else {
          console.warn('Aucun résultat Geocoding:', response.data);
          message.error(t('messages.noResults', { ns: 'ownerships', defaultValue: 'Aucune adresse trouvée pour cette localisation.' }));
          return;
        }
      } catch (error) {
        console.error('Erreur Geocoding API:', error);
        message.error(t('messages.geocodeError', { ns: 'ownerships', defaultValue: 'Erreur lors de la récupération de l\'adresse.' }));
        return;
      }

      if (!altitude) {
        try {
          const elevationResponse = await axios.get(
            `https://maps.googleapis.com/maps/api/elevation/json?locations=${latitude},${longitude}&key=${process.env.REACT_APP_GEOCODE_API_KEY}`,
          );
          console.log('Réponse Elevation API:', elevationResponse.data);

          if (elevationResponse?.data?.results?.length > 0) {
            updatedFields.altitude = elevationResponse.data.results[0].elevation;
            console.log('Altitude mise à jour:', updatedFields.altitude);
          } else {
            console.warn('Aucun résultat Elevation:', elevationResponse.data);
          }
        } catch (error) {
          console.error('Erreur Elevation API:', error);
          message.warning(t('messages.elevationError', { ns: 'ownerships', defaultValue: 'Impossible de récupérer l\'altitude.' }));
        }
      }

      formProps?.form?.setFieldsValue(updatedFields);
      message.success(t('messages.locationFetched', { ns: 'ownerships', defaultValue: 'Localisation récupérée avec succès' }));
    } catch (error: any) {
      console.error('Erreur de géolocalisation:', error);
      if (error.code === 1) {
        message.error(t('messages.geolocationDenied', { ns: 'ownerships', defaultValue: 'Activez votre localisation dans les paramètres de votre appareil ou navigateur.' }));
      } else if (error.code === 2 || error.code === 3) {
        message.error(t('messages.geolocationUnavailable', { ns: 'ownerships', defaultValue: 'Localisation non trouvée. Veuillez vérifier votre connexion GPS/Wi-Fi ou entrer l\'adresse manuellement.' }));
      } else {
        message.error(t('messages.geolocationError', { ns: 'ownerships', defaultValue: 'Erreur lors de la récupération de la localisation.' }));
      }
    } finally {
      setIsGeolocationLoading(false);
    }
  };

  const onNext = async (nextStep: 2 | 3 | 4) => {
    try {
      if (nextStep === 2) {
        await formProps?.form?.validateFields([
          'name',
          'type',
          'price',
          'short_description',
          'user_id',
        ]);
      } else if (nextStep === 3) {
        await formProps?.form?.validateFields([
          'address',
          'state',
          'province',
          'city',
        ]);
      } else if (nextStep === 4) {
        await formProps?.form?.validateFields([
          'surface',
          'availables',
          'living_rooms',
          'bathrooms',
          'length',
          'width',
        ]);
      }
      setStep(nextStep);
    } catch (error) {
      console.error(`Step ${step} validation error:`, error);
    }
  };

  const onBack = (prevStep: 1 | 2 | 3) => {
    setStep(prevStep);
  };

const onFinish = async () => {
  try {
    console.log('onFinish - Début de la soumission');
    const rawFormData = formProps?.form?.getFieldsValue();
    console.log('Raw form data:', JSON.stringify(rawFormData, null, 2));

    await formProps?.form?.validateFields();
    const formData: any = formProps?.form?.getFieldsValue();
    console.log('Form data after validation:', JSON.stringify(formData, null, 2));

    const requiredFields = [
      'name',
      'type',
      'address',
      'user_id',
      'price',
      'short_description',
      'state',
      'province',
      'city',
      'surface',
      'availables',
    ];
    const missingFields = requiredFields.filter(field => !formData[field]);
    if (missingFields.length > 0) {
      throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
    }

    if (!formData.latitude || !formData.longitude) {
      const response = await axios.get(
        `${process.env.REACT_APP_GEOCODE_API}?address=${encodeURIComponent(formData.address)}, ${formData.city || ''} (${formData.province || ''}) ${formData.state || ''}&key=${process.env.REACT_APP_GEOCODE_API_KEY}`,
      );
      if (response?.data?.results?.length > 0) {
        formData.latitude = response.data.results[0].geometry.location.lat;
        formData.longitude = response.data.results[0].geometry.location.lng;
      } else {
        console.warn('Aucun résultat Geocoding pour l\'adresse:', response.data);
      }
    }

    formProps?.form?.setFieldsValue(formData);
    await new Promise(resolve => setTimeout(resolve, 100));

    const finalFormData = formProps?.form?.getFieldsValue();
    console.log('Final form data before submit:', JSON.stringify(finalFormData, null, 2));

    // Soumission unique
    formProps?.form?.submit();
    console.log('onFinish - Soumission envoyée');
  } catch (error) {
    console.error('Form submission error:', error);
    message.error(t('messages.formSubmissionError', { ns: 'ownerships', defaultValue: 'Erreur lors de la soumission du formulaire.' }));
  }
};

  const stepTitles = {
    1: t('steps.basic_information', { ns: 'ownerships', defaultValue: 'Informations de base' }),
    2: t('steps.location_details', { ns: 'ownerships', defaultValue: 'Détails de localisation' }),
    3: t('steps.property_features', { ns: 'ownerships', defaultValue: 'Caractéristiques de la propriété' }),
    4: t('steps.media_and_additional_info', { ns: 'ownerships', defaultValue: 'Médias et informations supplémentaires' }),
  };

  return (
    <Create
      saveButtonProps={{
        ...saveButtonProps,
        onClick: step === 4 ? onFinish : () => onNext(step === 1 ? 2 : step === 2 ? 3 : 4),
        children: step < 4 ? t('buttons.next', { ns: 'ownerships' }) : t('buttons.save', { ns: 'ownerships' }),
      }}
      footerButtons={
        <>
          {step > 1 && (
            <Button onClick={() => onBack(step === 4 ? 3 : step === 3 ? 2 : 1)}>
              {t('buttons.back', { ns: 'ownerships' })}
            </Button>
          )}
          <Button
            type="primary"
            {...saveButtonProps}
            onClick={step === 4 ? onFinish : () => onNext(step === 1 ? 2 : step === 2 ? 3 : 4)}
          >
            {step < 4 ? t('buttons.next', { ns: 'ownerships' }) : t('buttons.save', { ns: 'ownerships' })}
          </Button>
        </>
      }
    >
      <style>
        {`
          .location-card {
            background: #fff;
            border-radius: 8px;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
            padding: 24px;
            margin-bottom: 24px;
          }
          .location-alert {
            background: #e6f7ff;
            border: 1px solid #91d5ff;
            border-radius: 4px;
            margin-bottom: 16px;
          }
          .geolocation-button {
            display: flex;
            align-items: center;
            gap: 8px;
          }
          .form-item-label {
            font-weight: 500;
            color: #595959;
          }
          @media (max-width: 576px) {
            .location-card {
              padding: 16px;
            }
            .geolocation-button {
              width: 100%;
              justify-content: center;
            }
          }
        `}
      </style>
      <Title level={4}>{stepTitles[step]}</Title>
      <Form
        {...formProps}
        initialValues={{
          user_id: authUser?.id,
          file_def_folder: fileFolder,
          state: 'BF',
          name: '',
          type: OwnershipType.Apartment,
          address: '',
          price: null,
          short_description: '',
          published: false,
          loti: false,
          electricity: false,
          water: false,
          parking: false,
          elevator: false,
          corridor: false,
          concierge: false,
          dogs_allowed: false,
          kitchen: false,
          wi_fi: false,
          safe_box: false,
          attachments: [],
        }}
        layout="vertical"
      >
        <Form.Item hidden name="file_def_folder">
          <Input type="hidden" />
        </Form.Item>
        <Form.Item hidden name="attachments">
          <Input type="hidden" />
        </Form.Item>
        <Row>
          {/* Étape 1 : Informations de base */}
          <Col xs={24} sm={24} md={12} lg={12} xl={12} style={{ display: step === 1 ? 'block' : 'none' }}>
            <Form.Item
              label={t('fields.name', { ns: 'ownerships' })}
              name="name"
              rules={[{ required: true, message: t('fields.lastname.required', { ns: 'ownerships' }) }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} style={{ display: step === 1 ? 'block' : 'none' }}>
            <Form.Item
              label={t('fields.type', { ns: 'ownerships' })}
              name="type"
              rules={[{ required: true, message: t('fields.type.required', { ns: 'ownerships' }) }]}
            >
              <Select
                options={typeOptions}
                onChange={value => setOwnershipType(value)}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} style={{ display: step === 1 ? 'block' : 'none' }}>
            <Form.Item
              label={t('fields.price', { ns: 'ownerships' })}
              name="price"
              rules={[{ required: true, message: t('fields.priceR', { ns: 'ownerships' }) }]}
            >
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} style={{ display: step === 1 ? 'block' : 'none' }}>
            <Form.Item
              label={t('fields.short_description', { ns: 'ownerships' })}
              name="short_description"
              rules={[{ required: true, message: t('fields.short_descriptionR', { ns: 'ownerships' }) }]}
            >
              <Input />
            </Form.Item>
          </Col>
          {authUser.role === UserRoleEnum.Admin || authUser.role === UserRoleEnum.Agency ? (
            <Col xs={24} sm={24} md={12} lg={12} xl={12} style={{ display: step === 1 ? 'block' : 'none' }}>
              <Form.Item
                label={t('fields.user_id', { ns: 'ownerships' })}
                name="user_id"
                rules={[{ required: true, message: t('fields.user_id.required', { ns: 'ownerships' }) }]}
              >
                <div>
                  <Select<BaseKey>
                    options={users}
                    onSearch={onSearchUser}
                    onChange={handleUserSelect}
                    defaultValue={selectPropsUser.defaultValue?.value as BaseKey}
                    style={{ width: 'calc(100% - 82px)', display: 'inline-block' }}
                    showSearch
                    allowClear
                    filterOption={false}
                  />
                  <Button
                    style={{ display: 'inline-block', width: '82px' }}
                    onClick={() => setNewUserVisible(true)}
                  >
                    {t('fields.new', { ns: 'ownerships' })}
                  </Button>
                </div>
              </Form.Item>
            </Col>
          ) : (
            <Form.Item name="user_id" hidden>
              <InputNumber value={authUser?.id} />
            </Form.Item>
          )}

          {/* Étape 2 : Détails de localisation */}
          <Col xs={24} style={{ display: step === 2 ? 'block' : 'none' }}>
            <Card className="location-card">
              <Alert
                message={t('messages.manualEntry', { ns: 'ownerships', defaultValue: 'Si la géolocalisation échoue, vous pouvez entrer l\'adresse manuellement.' })}
                type="info"
                showIcon
                className="location-alert"
              />
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={<span className="form-item-label">{t('fields.address', { ns: 'ownerships' })}</span>}
                    name="address"
                    rules={[{ required: true, message: t('fields.addressR', { ns: 'ownerships' }) }]}
                  >
                    <Input placeholder="Entrez l'adresse complète" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={<span className="form-item-label">{t('fields.city', { ns: 'ownerships' })}</span>}
                    name="city"
                    rules={[{ required: true, message: t('fields.cityR', { ns: 'ownerships' }) }]}
                  >
                    <Input placeholder="Entrez la ville" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={<span className="form-item-label">{t('fields.state', { ns: 'ownerships' })}</span>}
                    name="state"
                    rules={[{ required: true, message: t('fields.stateR', { ns: 'ownerships' }) }]}
                  >
                    <Select
                      options={country}
                      showSearch
                      onSearch={onSearchCountry}
                      onChange={value => setCountrySel(value)}
                      placeholder="Sélectionnez un pays"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={<span className="form-item-label">{t('fields.province', { ns: 'ownerships' })}</span>}
                    name="province"
                    rules={[{ required: true, message: t('fields.provinceR', { ns: 'ownerships' }) }]}
                  >
                    <Select
                      options={states}
                      showSearch
                      onSearch={onSearchState}
                      onChange={value => setStateSel(value)}
                      placeholder="Sélectionnez une province/région"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={<span className="form-item-label">{t('fields.postal_code', { ns: 'ownerships', defaultValue: 'Code postal' })}</span>}
                    name="postal_code"
                  >
                    <Input placeholder="Entrez le code postal" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={<span className="form-item-label">{t('fields.altitude', { ns: 'ownerships', defaultValue: 'Altitude (m)' })}</span>}
                    name="altitude"
                  >
                    <InputNumber style={{ width: '100%' }} disabled placeholder="Rempli automatiquement" />
                  </Form.Item>
                </Col>
                {authUser.role === UserRoleEnum.Admin ? (
                  <>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label={<span className="form-item-label">{t('fields.latitude', { ns: 'ownerships' })}</span>}
                        name="latitude"
                      >
                        <InputNumber style={{ width: '100%' }} placeholder="Entrez la latitude" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label={<span className="form-item-label">{t('fields.longitude', { ns: 'ownerships' })}</span>}
                        name="longitude"
                      >
                        <InputNumber style={{ width: '100%' }} placeholder="Entrez la longitude" />
                      </Form.Item>
                    </Col>
                  </>
                ) : (
                  <>
                    <Form.Item name="latitude" hidden>
                      <InputNumber />
                    </Form.Item>
                    <Form.Item name="longitude" hidden>
                      <InputNumber />
                    </Form.Item>
                  </>
                )}
                <Col xs={24}>
                  <Spin spinning={isGeolocationLoading}>
                    <Button
                      type="primary"
                      onClick={getCurrentLocation}
                      className="geolocation-button"
                      disabled={isGeolocationLoading}
                    >
                      <AimOutlined />
                      {t('buttons.getCurrentLocation', { ns: 'ownerships', defaultValue: 'Utiliser ma localisation actuelle' })}
                    </Button>
                  </Spin>
                </Col>
              </Row>
            </Card>
          </Col>

          {/* Étape 3 : Caractéristiques de la propriété */}
          <Col xs={12} sm={8} md={6} lg={4} xl={4} style={{ display: step === 3 ? 'block' : 'none' }}>
            <Form.Item name="published" valuePropName="checked" noStyle>
              <Checkbox>{t('fields.published', { ns: 'ownerships' })}</Checkbox>
            </Form.Item>
          </Col>
          <Col xs={12} sm={8} md={6} lg={4} xl={4} style={{ display: step === 3 ? 'block' : 'none' }}>
            <Form.Item name="loti" valuePropName="checked" noStyle>
              <Checkbox>{t('fields.loti', { ns: 'ownerships' })}</Checkbox>
            </Form.Item>
          </Col>
          <Col xs={12} sm={8} md={6} lg={4} xl={4} style={{ display: step === 3 ? 'block' : 'none' }}>
            <Form.Item name="electricity" valuePropName="checked" noStyle>
              <Checkbox>{t('fields.electricity', { ns: 'ownerships' })}</Checkbox>
            </Form.Item>
          </Col>
          <Col xs={12} sm={8} md={6} lg={4} xl={4} style={{ display: step === 3 ? 'block' : 'none' }}>
            <Form.Item name="water" valuePropName="checked" noStyle>
              <Checkbox>{t('fields.water', { ns: 'ownerships' })}</Checkbox>
            </Form.Item>
          </Col>
          {ownershipType && ownershipType !== OwnershipType.Ground && (
            <>
              <Col xs={12} sm={8} md={6} lg={4} xl={4} style={{ display: step === 3 ? 'block' : 'none' }}>
                <Form.Item name="parking" valuePropName="checked" noStyle>
                  <Checkbox>{t('fields.parking', { ns: 'ownerships' })}</Checkbox>
                </Form.Item>
              </Col>
              <Col xs={12} sm={8} md={6} lg={4} xl={4} style={{ display: step === 3 ? 'block' : 'none' }}>
                <Form.Item name="elevator" valuePropName="checked" noStyle>
                  <Checkbox>{t('fields.elevator', { ns: 'ownerships' })}</Checkbox>
                </Form.Item>
              </Col>
              <Col xs={12} sm={8} md={6} lg={4} xl={4} style={{ display: step === 3 ? 'block' : 'none' }}>
                <Form.Item name="corridor" valuePropName="checked" noStyle>
                  <Checkbox>{t('fields.corridor', { ns: 'ownerships' })}</Checkbox>
                </Form.Item>
              </Col>
              <Col xs={12} sm={8} md={6} lg={4} xl={4} style={{ display: step === 3 ? 'block' : 'none' }}>
                <Form.Item name="concierge" valuePropName="checked" noStyle>
                  <Checkbox>{t('fields.concierge', { ns: 'ownerships' })}</Checkbox>
                </Form.Item>
              </Col>
              <Col xs={12} sm={8} md={6} lg={4} xl={4} style={{ display: step === 3 ? 'block' : 'none' }}>
                <Form.Item name="dogs_allowed" valuePropName="checked" noStyle>
                  <Checkbox>{t('fields.dogs_allowed', { ns: 'ownerships' })}</Checkbox>
                </Form.Item>
              </Col>
              <Col xs={12} sm={8} md={6} lg={4} xl={4} style={{ display: step === 3 ? 'block' : 'none' }}>
                <Form.Item name="kitchen" valuePropName="checked" noStyle>
                  <Checkbox>{t('fields.kitchen', { ns: 'ownerships' })}</Checkbox>
                </Form.Item>
              </Col>
            </>
          )}
          {ownershipType === OwnershipType.Hotel && (
            <>
              <Col xs={12} sm={8} md={6} lg={4} xl={4} style={{ display: step === 3 ? 'block' : 'none' }}>
                <Form.Item name="wi_fi" valuePropName="checked" noStyle>
                  <Checkbox>{t('fields.wi_fi', { ns: 'ownerships' })}</Checkbox>
                </Form.Item>
              </Col>
              <Col xs={12} sm={8} md={6} lg={4} xl={4} style={{ display: step === 3 ? 'block' : 'none' }}>
                <Form.Item name="safe_box" valuePropName="checked" noStyle>
                  <Checkbox>{t('fields.safe_box', { ns: 'ownerships' })}</Checkbox>
                </Form.Item>
              </Col>
            </>
          )}
          {ownershipType && (
            <Col xs={24} sm={24} md={12} lg={12} xl={12} style={{ display: step === 3 ? 'block' : 'none' }}>
              <Form.Item
                label={t('fields.surface', { ns: 'ownerships' })}
                name="surface"
                rules={[{ required: true, message: t('fields.surfaceR', { ns: 'ownerships' }) }]}
              >
                <InputNumber style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          )}
          <Col xs={24} sm={24} md={12} lg={12} xl={12} style={{ display: step === 3 ? 'block' : 'none' }}>
            <Form.Item
              label={t('fields.availables', { ns: 'ownerships' })}
              name="availables"
              rules={[{ required: true, message: t('fields.availablesR', { ns: 'ownerships' }) }]}
            >
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          {ownershipType && ownershipType !== OwnershipType.Ground && (
            <>
              <Col xs={24} sm={24} md={12} lg={12} xl={12} style={{ display: step === 3 ? 'block' : 'none' }}>
                <Form.Item
                  label={t('fields.living_rooms', { ns: 'ownerships' })}
                  name="living_rooms"
                  rules={[{ required: true, message: t('fields.living_roomsR', { ns: 'ownerships' }) }]}
                >
                  <InputNumber style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12} lg={12} xl={12} style={{ display: step === 3 ? 'block' : 'none' }}>
                <Form.Item
                  label={t('fields.dining_rooms', { ns: 'ownerships' })}
                  name="dining_rooms"
                >
                  <InputNumber style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12} lg={12} xl={12} style={{ display: step === 3 ? 'block' : 'none' }}>
                <Form.Item
                  label={t('fields.rooms', { ns: 'ownerships' })}
                  name="rooms"
                  rules={[{ required: true, message: t('fields.roomsR', { ns: 'ownerships' }) }]}
                >
                  <InputNumber style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12} lg={12} xl={12} style={{ display: step === 3 ? 'block' : 'none' }}>
                <Form.Item
                  label={t('fields.bathrooms', { ns: 'ownerships' })}
                  name="bathrooms"
                  rules={[{ required: true, message: t('fields.bathroomsR', { ns: 'ownerships' }) }]}
                >
                  <InputNumber style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12} lg={12} xl={12} style={{ display: step === 3 ? 'block' : 'none' }}>
                <Form.Item
                  label={t('fields.beds', { ns: 'ownerships' })}
                  name="beds"
                >
                  <InputNumber style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12} lg={12} xl={12} style={{ display: step === 3 ? 'block' : 'none' }}>
                <Form.Item
                  label={t('fields.magazine', { ns: 'ownerships' })}
                  name="magazine"
                >
                  <InputNumber style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12} lg={12} xl={12} style={{ display: step === 3 ? 'block' : 'none' }}>
                <Form.Item
                  label={t('fields.stairs', { ns: 'ownerships' })}
                  name="stairs"
                >
                  <InputNumber style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12} lg={12} xl={12} style={{ display: step === 3 ? 'block' : 'none' }}>
                <Form.Item
                  label={t('fields.floors', { ns: 'ownerships' })}
                  name="floors"
                >
                  <InputNumber style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12} lg={12} xl={12} style={{ display: step === 3 ? 'block' : 'none' }}>
                <Form.Item
                  label={t('fields.air_or_heating', { ns: 'ownerships' })}
                  name="air_or_heating"
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12} lg={12} xl={12} style={{ display: step === 3 ? 'block' : 'none' }}>
                <Form.Item
                  label={t('fields.outdoor_space', { ns: 'ownerships' })}
                  name="outdoor_space"
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12} lg={12} xl={12} style={{ display: step === 3 ? 'block' : 'none' }}>
                <Form.Item
                  label={t('fields.internal', { ns: 'ownerships' })}
                  name="internal"
                >
                  <Input />
                </Form.Item>
              </Col>
            </>
          )}
          {(ownershipType === OwnershipType.Villa || ownershipType === OwnershipType.Apartment) && (
            <Col xs={24} sm={24} md={12} lg={12} xl={12} style={{ display: step === 3 ? 'block' : 'none' }}>
              <Form.Item
                label={t('fields.offices', { ns: 'ownerships' })}
                name="offices"
              >
                <InputNumber style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          )}
          {ownershipType === OwnershipType.Hotel && (
            <>
              <Col xs={24} sm={24} md={12} lg={12} xl={12} style={{ display: step === 3 ? 'block' : 'none' }}>
                <Form.Item
                  label={t('fields.bedroom_services', { ns: 'ownerships' })}
                  name="bedroom_services"
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12} lg={12} xl={12} style={{ display: step === 3 ? 'block' : 'none' }}>
                <Form.Item
                  label={t('fields.bath_services', { ns: 'ownerships' })}
                  name="bath_services"
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12} lg={12} xl={12} style={{ display: step === 3 ? 'block' : 'none' }}>
                <Form.Item
                  label={t('fields.kitchen_services', { ns: 'ownerships' })}
                  name="kitchen_services"
                >
                  <Input />
                </Form.Item>
              </Col>
            </>
          )}
          {ownershipType === OwnershipType.Ground && (
            <>
              <Col xs={24} sm={24} md={12} lg={12} xl={12} style={{ display: step === 3 ? 'block' : 'none' }}>
                <Form.Item
                  label={t('fields.length', { ns: 'ownerships' })}
                  name="length"
                  rules={[{ required: true, message: t('fields.length.required', { ns: 'ownerships' }) }]}
                >
                  <InputNumber style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12} lg={12} xl={12} style={{ display: step === 3 ? 'block' : 'none' }}>
                <Form.Item
                  label={t('fields.width', { ns: 'ownerships' })}
                  name="width"
                  rules={[{ required: true, message: t('fields.widthR', { ns: 'ownerships' }) }]}
                >
                  <InputNumber style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </>
          )}

          {/* Étape 4 : Médias et informations supplémentaires */}
          <Col span={24} style={{ display: step === 4 ? 'block' : 'none' }}>
            <Form.Item label={t('fields.images', { ns: 'ownerships' })}>
              <Upload
                customRequest={customRequest}
                listType="picture-card"
                accept="image/*"
                onChange={handleChange}
                onRemove={handleRemove}
                fileList={fileList}
                onPreview={onPreview}
                maxCount={5}
                beforeUpload={(file) => {
                  console.log('beforeUpload - Fichier sélectionné:', file);
                  return true;
                }}
              >
                {fileList.length < 5 && '+ Upload'}
              </Upload>
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} style={{ display: step === 4 ? 'block' : 'none' }}>
            <Form.Item
              label={t('fields.description', { ns: 'ownerships' })}
              name="description"
            >
              <Input.TextArea />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} style={{ display: step === 4 ? 'block' : 'none' }}>
            <Form.Item
              label={t('fields.emergency_infos', { ns: 'ownerships' })}
              name="emergency_infos"
            >
              <Input.TextArea />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} style={{ display: step === 4 ? 'block' : 'none' }}>
            <Form.Item
              label={t('fields.extra_infos', { ns: 'ownerships' })}
              name="extra_infos"
            >
              <Input.TextArea />
            </Form.Item>
          </Col>
        </Row>
      </Form>
      <Modal
        title={t('fields.new_owner', { ns: 'ownerships' })}
        visible={newUserVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        cancelButtonProps={{ hidden: true }}
        width={1000}
      >
        {newUserError && (
          <Alert
            message={t('fields.create_user_error', { ns: 'ownerships' })}
            type="error"
            showIcon
            closable
          />
        )}
        <Form
          form={form}
          onFieldsChange={newUserForm}
          name="new_user_form"
          layout="vertical"
        >
          <Row gutter={[16, 16]}>
            <Col className="gutter-row" xs={24} sm={24} md={24} lg={12} xl={12}>
              <Form.Item
                label={t('fields.lastname', { ns: 'users' })}
                name="lastname"
                rules={[{ required: true, message: 'Veuillez entrer le nom de famille' }]}
              >
                <Input maxLength={255} />
              </Form.Item>
            </Col>
            <Col className="gutter-row" xs={24} sm={24} md={24} lg={12} xl={12}>
              <Form.Item
                label={t('fields.firstname', { ns: 'users' })}
                name="firstname"
                rules={[{ required: true, message: 'Veuillez entrer le prénom' }]}
              >
                <Input maxLength={255} />
              </Form.Item>
            </Col>
            <Col className="gutter-row" xs={24} sm={24} md={24} lg={12} xl={12}>
              <Form.Item
                label={t('fields.email', { ns: 'users' })}
                name="email"
                rules={[
                  { type: 'email', message: 'L\'email n\'est pas valide !' },
                  { required: !newUserPhone, message: 'Veuillez entrer l\'email' },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col className="gutter-row" xs={24} sm={24} md={24} lg={12} xl={12}>
              <Form.Item
                label={t('fields.phone', { ns: 'users' })}
                name="phone"
                rules={[{ required: !newUserEmail, message: 'Veuillez entrer le téléphone' }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </Create>
  );
};